import { useCallback, useEffect } from 'react';
import { t } from '../locale';

import {
    MediaPermissionsError,
    MediaPermissionsErrorType,
    requestMediaPermissions
} from 'mic-check';

/**
 *
 * Capture snapshot from web camera (document image, selfy image)
 *
 * @param video
 * @param imageType
 * @param selfImageData
 * @param documentImageData
 * @param capturedImagesCount
 * @returns
 */
export async function captureSnapshot(video: HTMLVideoElement, imageType: string, documentImageData: string) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context?.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
    const imageData = dataUrl.replace(/^data:image\/jpeg;base64,/, '');

    if (imageType === 'DOCUMENT') {
        documentImageData = imageData;
    }

    return { documentImageData };
}

/**
 *
 * @param imageType
 * @param selfImageData
 * @param documentImageData
 * @returns
 */
export function getCapturedImageData(imageType: string, selfImageData: string, documentImageData: string) {
    return imageType === 'SELF' ? selfImageData : documentImageData
}

/**
 *
 * @param selfImageData
 * @param documentImageData
 * @param companyId
 * @param requestId
 * @param toggleSelfIdentification
 * @returns
 */
export async function selfIdentificationRequest(selfImageData: string, documentImageData: string, companyId: string, requestId: string) {

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/self-identification`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                companyId: companyId,
                requestId: requestId,
                documentImage: documentImageData,
                selfImage: selfImageData
            })
        });

        const responseData = response.json();
        return responseData;
    } catch (error) {
        console.error('Error Self Identification: ', error);
    }

}

export async function getReferenceNumber(companyId: string, requestId: string) {
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/api/v1/request/data/all", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ requestId, companyId })
        });

        if (response.ok) {
            const data = await response.json();
            return data['referenceNumber'];
        } else {
            return '';
        }
    } catch (error) {
        console.error("Error while loading reference nuber: ", error);
    }
}

export async function checkRequest(companyId: string, requestId: string): Promise<boolean> {
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/api/v1/request/data/all", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ requestId, companyId })
        });
        return response.status === 404 ? false : true;
    } catch (error) {
        // Handle any errors, such as network issues or server errors
        console.error("Error while checking request:", error);
        return false;
    }
}

export function formatDate(date: Date | null) {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`
}

export function parseDate(date: string): Date | null {
    if (!date) return null;
    const parts = date.split(/[-./]/);
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    if (!day || !month || !year || year.length < 4) return null;
    const d = new Date(`${year}-${month}-${day}`);
    return isNaN(d.getTime()) ? null : d;
}

export async function updateStatus(companyId: string, requestId: string, status: string, origin: string, accessToken?: string | null) {
    try {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };
        if (accessToken) {
            headers["accessToken"] = accessToken;
        }
        await fetch(`${process.env.REACT_APP_API_URL}/api/v1/status/update`, {
            method: "POST",
            headers,
            body: JSON.stringify({
                companyId: companyId,
                requestId: requestId,
                status: status,
                origin: origin
            }),
        });
    } catch (err) {
        console.error("Update request status failed: " + err);
    }
}

export function useCamera(facingMode: any, device: any, videoElementIds: string[] = ["videoElement"]) {

    const streamCamVideo = useCallback(async () => {
        try {
            const videos = videoElementIds
                .map(id => document.getElementById(id) as HTMLVideoElement)
                .filter(Boolean);

            if (videos.length === 0) return;

            let constraints;
            if (device) {
                constraints = {
                    audio: false,
                    video: {
                        width: videos[0].videoWidth,
                        height: videos[0].videoHeight,
                        facingMode: { ideal: facingMode }
                    }
                };
            } else {
                constraints = {
                    audio: false,
                    video: {
                        width: { ideal: 2560 },
                        height: { ideal: 1440 },
                        facingMode: { ideal: facingMode }
                    }
                };
            }

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

            videos.forEach(video => {
                video.srcObject = mediaStream;
                video.controls = false;
                video.onloadedmetadata = function () {
                    video.play();
                };
            });

        } catch (err) {
            console.log(err);
        }
    }, [facingMode, device, videoElementIds]);

    useEffect(() => {

        streamCamVideo();
        return () => {
            videoElementIds.forEach(id => {
                const video = document.getElementById(id) as HTMLVideoElement;
                if (video && video.srcObject) {
                    const tracks = (video.srcObject as MediaStream).getTracks();
                    tracks.forEach(track => track.stop());
                }
            });
        };
    }, [streamCamVideo, videoElementIds]);
}

export function useCamera2(facingMode: any, device: any, videoElementIds: string[] = ["videoElement2"]) {

    const streamCamVideo = useCallback(async () => {
        try {
            const videos = videoElementIds
                .map(id => document.getElementById(id) as HTMLVideoElement)
                .filter(Boolean);

            if (videos.length === 0) return;

            let constraints;
            if (device) {
                constraints = {
                    audio: false,
                    video: {
                        width: videos[0].videoWidth,
                        height: videos[0].videoHeight,
                        facingMode: { ideal: facingMode }
                    }
                };
            } else {
                constraints = {
                    audio: false,
                    video: {
                        width: { ideal: 2560 },
                        height: { ideal: 1440 },
                        facingMode: { ideal: facingMode }
                    }
                };
            }

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

            videos.forEach(video => {
                video.srcObject = mediaStream;
                video.controls = false;
                video.onloadedmetadata = function () {
                    video.play();
                };
            });

        } catch (err) {
            console.log(err);
        }
    }, [facingMode, device, videoElementIds]);

    useEffect(() => {

        streamCamVideo();
        return () => {
            videoElementIds.forEach(id => {
                const video = document.getElementById(id) as HTMLVideoElement;
                if (video && video.srcObject) {
                    const tracks = (video.srcObject as MediaStream).getTracks();
                    tracks.forEach(track => track.stop());
                }
            });
        };
    }, [streamCamVideo, videoElementIds]);
}

export async function createJob(companyId: string, requestId: string, setReferenceNumber: any) {

    const refNumber = await getReferenceNumber(companyId, requestId);
    setReferenceNumber(refNumber);

    await fetch(`${process.env.REACT_APP_API_URL}/api/v1/job`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            companyId: companyId,
            requestId: requestId,
            paymentMethod: "direct"
        })
    })
}

export async function saveImage(image: string, companyId: string, requestId: string, test: boolean, testType?: string) {
    try {

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/document-back/ocr`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                image: image,
                companyId: companyId,
                requestId: requestId,
                test: test,
                testType: testType
            }),
        });

        const res = await response.json();
        return res;

    } catch (err) {
        console.error('Saving Image error: ' + err);
    }
}

// export async function saveVideo(blob: Blob, companyId: string, requestId: string, format: string) {
//     try {
//         let response: any;
//         const reader = new FileReader();
//         reader.readAsDataURL(blob);
//         reader.onloadend = async () => {
//             const base64data = reader.result?.toString().split(',')[1];  // Uzimanje base64 stringa

//             response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/video`, {
//                 method: "POST",
//                 headers: {
//                     "Content-Type": "application/json",
//                 },
//                 body: JSON.stringify({
//                     videoData: base64data,
//                     companyId: companyId,
//                     requestId: requestId,
//                     format: format
//                 }),
//             });

//             const res =  await response.json()
//             console.log('res: ' + res.status)
//             return res.status
//         }
//     } catch (err) {
//         console.error('Error uploading video:', err);
//     }
// }

export async function saveVideo(blob: Blob, companyId: string, requestId: string, format: string): Promise<boolean> {
    try {
        const base64data = await convertBlobToBase64(blob);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/video/upload`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                videoData: base64data,
                companyId,
                requestId,
                format
            }),
        });

        const res = await response.json();

        return res.status;
    } catch (err) {
        console.error('Error uploading video:', err);
        return false;
    }
}

function convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
            const base64String = reader.result?.toString().split(',')[1];
            if (base64String) {
                resolve(base64String);
            } else {
                reject(new Error('Failed to convert blob to Base64'));
            }
        };
        reader.onerror = (error) => reject(error);
    });
}


export async function checkVideo(companyId: string, requestId: string) {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/video/check`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            companyId: companyId,
            requestId: requestId
        })
    });

    return response.json();
}

export function setAccessToken(accessToken: string) {
    sessionStorage.setItem('accessToken', accessToken)
}

export function setUserAccess(userAccess: AdminAccess) {
    sessionStorage.setItem('userAccess', JSON.stringify(userAccess))
}

export function getRedirectUrl() {
    return sessionStorage.getItem('redirectUrl') ?? '/'
}

export function getAccessToken() {
    return sessionStorage.getItem('accessToken')
}

export function setCompanyInfo(info: { companyId: string, companyName: string, impersonationToken: string }) {
    sessionStorage.setItem('companyInfo', JSON.stringify(info))
    sessionStorage.setItem('impersonationToken', info.impersonationToken)
}

export function getCompanyInfo(): { companyId: string, companyName: string } | null {
    const raw = sessionStorage.getItem('companyInfo')
    return raw ? JSON.parse(raw) : null
}

export function removeAccess() {
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('userAccess')
    sessionStorage.removeItem('companyInfo')
    sessionStorage.removeItem('impersonationToken')
    sessionStorage.removeItem('redirectUrl')
}

let kAlertHideTimer: ReturnType<typeof setTimeout> | null = null
let kAlertSequence = 0

function clearKAlertHideTimer() {
    if (!kAlertHideTimer) return

    clearTimeout(kAlertHideTimer)
    kAlertHideTimer = null
}

function resolveAlertDuration(duration?: number | { autoHideMs?: number }) {
    if (typeof duration === 'number') return duration
    return duration?.autoHideMs
}

export function showKAlert(
    variant: string,
    message: string | Array<string>,
    duration?: number | { autoHideMs?: number },
) {
    clearKAlertHideTimer()

    kAlertSequence += 1
    const currentAlertSequence = kAlertSequence

    window.kAlert = {
        variant: variant,
        message: message
    }

    window.dispatchEvent(new Event('showKAlert'))

    const autoHideMs = resolveAlertDuration(duration)
    if (typeof autoHideMs === 'number' && autoHideMs > 0) {
        kAlertHideTimer = setTimeout(() => {
            if (currentAlertSequence !== kAlertSequence) return
            hideKAlert()
        }, autoHideMs)
    }
}
export function hideKAlert() {
    clearKAlertHideTimer()
    kAlertSequence += 1
    window.dispatchEvent(new Event('hideKAlert'))
}

export function disableClickables() {
    document.querySelectorAll('button, a, input, select, textarea')
        .forEach((item: any) => {
            item.classList.add('non-clickable')
            item.disabled = true
        })
}

export async function apiRequest(url: string, method: string, body?: any, apiKey?: string, accessToken?: any) {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json'
    };

    if (apiKey) {
        headers['x-api-key'] = `${apiKey}`
    }

    if (accessToken) {
        headers['accessToken'] = `${accessToken}`
    }

    const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined
    });

    // if (response.status === 403) {
    //     const navigate = useNavigate();
    //     navigate('/ds_id_verification/login')
    // }


    return response.json();
}


export async function getRequestData(companyId: string, requestId: string) {
    const response = await apiRequest(`${process.env.REACT_APP_API_URL}/api/v1/request/data/all`,
        'POST',
        {
            companyId: companyId,
            requestId: requestId,
        }
    )

    return response
}

export function scrollToBottom() {
    setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 150);
}


// export function resizeImage(file: File, maxWidth: number, maxHeight: number, callback: (base64: string) => void) {
//     const reader = new FileReader();

//     reader.onloadend = () => {
//         const img = new Image();
//         img.src = reader.result as string;

//         img.onload = () => {
//             const canvas = document.createElement('canvas');
//             let width = img.width;
//             let height = img.height;

//             // Proveri proporcije i prilagodi veličinu
//             if (width > height) {
//                 if (width > maxWidth) {
//                     height *= maxWidth / width;
//                     width = maxWidth;
//                 }
//             } else {
//                 if (height > maxHeight) {
//                     width *= maxHeight / height;
//                     height = maxHeight;
//                 }
//             }

//             // Postavi canvas dimenzije
//             canvas.width = width;
//             canvas.height = height;

//             // Iscrtaj sliku na canvas sa smanjenom rezolucijom
//             const ctx = canvas.getContext('2d');
//             if (ctx) {
//                 ctx.drawImage(img, 0, 0, width, height);

//                 // Konvertuj canvas nazad u Base64 format
//                 const base64String = canvas.toDataURL('image/png'); // Može biti i 'image/png'
//                 const imageData = base64String.replace(/^data:image\/png;base64,/, '');
//                 callback(imageData);
//             }
//         };
//     };

//     return reader.readAsDataURL(file);
// }

export function resizeImage(
    file: File,
    maxWidth: number,
    maxHeight: number,
    callback: (base64: string) => void
) {
    const reader = new FileReader();

    reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = () => {
            let width = img.width;
            let height = img.height;

            // Ako je slika veća od max, smanji je proporcionalno
            if (width > maxWidth || height > maxHeight) {
                const aspectRatio = width / height;

                if (width > height) {
                    width = maxWidth;
                    height = Math.round(maxWidth / aspectRatio);
                } else {
                    height = maxHeight;
                    width = Math.round(maxHeight * aspectRatio);
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);

                const base64String = canvas.toDataURL('image/jpeg', 0.85);
                const imageData = base64String.replace(/^data:image\/jpeg;base64,/, '');

                callback(imageData);
            }
        };
    };

    reader.readAsDataURL(file);
}

export const stripDataUrlPrefix = (dataUrl: string): string =>
    dataUrl.replace(/^data:image\/[a-zA-Z0-9.+-]+;base64,/, '')

export const checkPermission = () => {
    requestMediaPermissions()
        .then(() => {

        })
        .catch((err: MediaPermissionsError) => {
            const { type, name, message } = err;
            if (type === MediaPermissionsErrorType.SystemPermissionDenied) {
                showKAlert('danger', t('browserPermissionError'));
            } else if (type === MediaPermissionsErrorType.UserPermissionDenied) {
                showKAlert('danger', t('browserDeniedPermission'));
                disableClickables();
            } else if (type === MediaPermissionsErrorType.CouldNotStartVideoSource) {
                showKAlert('danger', t('browserCameraUsed'));
            } else {
                showKAlert('danger', t('cannotAccessCamera'));
            }
        })
}